'use client'

import { useState } from 'react'
import { AnimateNumber } from 'motion-plus/react'

import DynamicIcon, { IconName } from '@/components/DynamicIcon'
import { RevealSection } from '@/components/RevealSection'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { MembershipLevel, User } from '@/payload-types'
import { formatMoney } from '@/utilities/formatMoney'
import { cn } from '@/utilities/ui'

import { JoinButton } from './JoinButton'
import { MembershipColorSchemes } from './themes'
import { JoinButtonIntent } from './types'

export const MobileTierDisplay: React.FC<{
  tiers: MembershipLevel[]
  user: User | null
  userMemberLevel: MembershipLevel | null
  onJoinClick: (chosenPlan: MembershipLevel, intent: JoinButtonIntent) => Promise<void>
}> = ({ tiers, user, userMemberLevel, onJoinClick }) => {
  const tierCards = tiers.sort((a, b) => a.price - b.price)
  const defaultTier = tierCards.find((level) => level.mostPopular) ?? tiers[0]
  const [selectedTier, setSelectedTier] = useState<MembershipLevel>(defaultTier)

  const changeSelection = (value: string) => {
    const selectedTier = tiers.find((level) => level.id.toString() === value) ?? tiers[0]
    setSelectedTier(selectedTier)
  }

  return (
    <div className="flex flex-col gap-4">
      <RevealSection
        once
        className="flex flex-col gap-2 rounded-xl border border-border bg-primary-foreground p-1"
      >
        <RadioGroup
          className="gap-0.5"
          onValueChange={changeSelection}
          value={selectedTier.id.toString()}
        >
          {tierCards.map((tier) => {
            const id = `mobile-card-${tier.id}`

            return (
              <div
                key={tier.id}
                className={cn(
                  'flex items-center gap-3 rounded-xl px-2 py-3',
                  selectedTier.id === tier.id && 'bg-primary/5',
                )}
              >
                <RadioGroupItem value={tier.id.toString()} id={id} />
                <Label
                  htmlFor={id}
                  className="mb-0 flex w-full cursor-pointer flex-row items-center justify-between gap-2 text-primary"
                >
                  <div className="font-display text-lg leading-none font-bold">{tier.title}</div>
                  <div className="flex flex-col items-end">
                    {tier.mostPopular && (
                      <div className="mb-1 rounded-xl bg-evergreen-700 px-2 py-0.5 text-xs text-primary-foreground">
                        Most Popular
                      </div>
                    )}
                    {tier.price === 0 ? 'Free' : `$${tier.price}`}
                  </div>
                </Label>
              </div>
            )
          })}
        </RadioGroup>
      </RevealSection>
      {selectedTier && (
        <>
          <RevealSection
            once
            className="flex flex-col gap-2 space-y-2 rounded-xl border border-border bg-primary-foreground p-4"
          >
            <h2 className="mb-0 font-display text-lg font-bold">{selectedTier.title}</h2>
            <div className="mb-0 font-display text-lg font-bold">
              {selectedTier.price > 0 && (
                <>
                  <AnimateNumber
                    format={{
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0,
                    }}
                    locales="en-US"
                    suffix="/ month"
                    className="flex items-center font-bold [&_.number-section-post]:ml-1.5 [&_.number-section-post]:self-center [&_.number-section-post]:font-body [&_.number-section-post]:text-sm [&_.number-section-post]:font-medium"
                  >
                    {selectedTier.price}
                  </AnimateNumber>
                  <p
                    className={cn(
                      'items-center font-body text-xs',
                      MembershipColorSchemes.white.text,
                    )}
                  >
                    <span className="text-nowrap">
                      Billed annually at {formatMoney(selectedTier.price * 12)}/yr.
                    </span>
                  </p>
                </>
              )}
            </div>
            {selectedTier.description && (
              <p className={'font-body text-sm text-pretty'}>{selectedTier.description}</p>
            )}
            {selectedTier.benefits && selectedTier.benefits.length > 0 && (
              <>
                <div className={'h-px w-full bg-evergreen-900/30'} />
                {selectedTier.benefits.map((benefit) => (
                  <div key={benefit.id} className="flex items-start gap-2">
                    <DynamicIcon
                      name={benefit.icon as IconName}
                      className="mt-1 mr-1 size-4 shrink-0"
                    />
                    <p className="text-sm text-pretty">{benefit.benefitItem}</p>
                  </div>
                ))}
              </>
            )}
          </RevealSection>
          <RevealSection once>
            <JoinButton
              tierData={selectedTier}
              user={user}
              userMemberLevel={userMemberLevel}
              colorScheme={MembershipColorSchemes.white}
              onClick={onJoinClick}
              className="w-full"
            />
          </RevealSection>
        </>
      )}
    </div>
  )
}
